import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import ActionButton from '@repo/ui/action-button'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { PenIcon, PlusIcon, SettingIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

import { API } from '../../../api/apis.config'
import { createHobby } from '../../../api/hobbies/create-hobby'
import { updateHobby } from '../../../api/hobbies/update-hobby'
import FilterDrawer from '../../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../../components/elements/dynamic-table'
import { countFilters } from '../../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../../components/elements/nav-bar'
import HobbiesFilter from '../../../components/pages/hobby/hobbies-filter'
import HobbiesForm from '../../../components/pages/hobby/hobby-form'
import { useFilters } from '../../../lib/hooks/use-filters'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { hobbyAudienceEnumMapping } from '../../../lib/types/enums/hobby/hobby-audience.enum'
import { HobbiesFilter as HobbiesFilterType, Hobby } from '../../../lib/types/models/hobby/hobby'
import { HobbyInput } from '../../../lib/types/models/hobby/hobby-input'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import {
  transformHobbyFiltersToUrl,
  transformUrlToHobbyFilters
} from '../../../lib/utils/filters-mapping/hobby'
import { toHobbyInput } from '../../../lib/utils/functions/hobby.functions'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'

const HobbiesPage = () => {
  const tableRef = useRef<DynamicTableRef<Hobby>>(null)
  const [isTableLoading, setIsTableLoading] = useState(false)

  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'edit' | 'create' | 'filters',
    Hobby
  >(['edit', 'create', 'filters'])

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<HobbiesFilterType>({
      initialFilters: { automaticallyGenerated: false },
      options: { allowSearchParam: true },
      transformFilterToUrl: transformHobbyFiltersToUrl,
      transformUrlToFilter: transformUrlToHobbyFilters
    })

  const { mutateAsync: mutateEdit } = useMutation({
    mutationFn: updateHobby,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('edit')
      toast.success('Loisir modifié avec succès')
    }
  })

  const onSubmitEdit = async (values: HobbyInput) => {
    if (selectedData) await mutateEdit({ id: selectedData.id, ...values })
  }

  const { mutateAsync } = useMutation({
    mutationFn: createHobby,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('create')
      toast.success('Loisir créé avec succès')
    }
  })

  const columns: Column<Hobby>[] = [
    {
      accessorKey: 'id',
      header: 'Id',
      enableSorting: true
    },
    {
      accessorKey: 'name',
      header: 'NOM',
      enableSorting: true
    },
    {
      accessorKey: 'description',
      header: 'Description',
      enableSorting: true,
      cell: (data) => valueRender(data.row.original.description)
    },
    {
      accessorKey: 'visibleTo',
      header: 'Visible dans',
      cell: (data) => {
        const visibleTo = data.row.original.visibleTo
        if (!visibleTo?.length) return '-'
        return (
          <div className="flex gap-2 items-center flex-wrap py-2">
            {visibleTo.map((audience) => (
              <Badge
                key={audience}
                variant="grey"
                textColor={tailwindColors.grey.DEFAULT}
                text={hobbyAudienceEnumMapping[audience]}
              />
            ))}
          </div>
        )
      }
    },
    {
      accessorKey: 'picto',
      header: 'Picto',
      cell: (data) => {
        const picto = data.row.original.picto
        if (!picto) return '-'
        return (
          <img src={picto.path} alt={picto.name} className="w-6 h-6 rounded-full object-cover" />
        )
      }
    },
    {
      accessorKey: 'id',
      id: 'actions',
      header: '',
      cell: (data) => (
        <ActionButton
          variant={'grey'}
          icon={PenIcon}
          onClick={() => toggleModal('edit', data.row.original)}
        />
      )
    }
  ]

  return (
    <div>
      <UserNavBar title="Réponses question des loisirs" icon={SettingIcon} />
      <div className="h-4" />
      <DynamicDataTable<Hobby, HobbiesFilterType>
        ref={tableRef}
        title="Réponses question des loisirs"
        columns={columns}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        onFiltersClick={() => toggleModal('filters')}
        onLoadingChange={setIsTableLoading}
        deleteMessage={{ name: 'loisir', attributeName: 'name' }}
        selectable
        allowSearchParam
        endpoint={API.hobbies.getAll.url}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            label: 'Créer',
            fn: () => toggleModal('create')
          }
        ]}
      />

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <HobbiesFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>

      {/* Edit */}
      <Modal
        icon={SettingIcon}
        isOpen={modalState.edit}
        title="Modifier"
        hideActions
        onCancel={() => toggleModal('edit')}
      >
        <HobbiesForm
          className={MODAL_BODY_CLASSNAME}
          defaultValues={toHobbyInput(selectedData)}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('edit'),
            onConfirm: async (values) => await onSubmitEdit(values)
          })}
        />
      </Modal>

      {/* Create */}
      <Modal
        icon={SettingIcon}
        isOpen={modalState.create}
        title="Créer"
        hideActions
        onCancel={() => toggleModal('create')}
      >
        <HobbiesForm
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('create'),
            onConfirm: async (values) => await mutateAsync(values)
          })}
        />
      </Modal>
    </div>
  )
}

export default HobbiesPage
